import { Link } from "react-router-dom";
import { matchScoreClasses } from "@/lib/matchScore";
import { memo, useState } from "react";
import { Bed, Bath, MapPin, Heart, User, Users, Footprints, BadgeCheck } from "lucide-react";
import type { Property } from "@/hooks/useProperties";
import { cardTags } from "@/lib/amenities";
import { useIsMobile } from "@/hooks/use-mobile";
import Img from "@/components/Img";
import { useTenant } from "@/hooks/useTenant";
import { PRICE_ON_REQUEST, formatRent, hasPrice, tenantCurrency, type Currency } from "@/lib/money";
import { usePromotions } from "@/lib/monetization";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

interface PropertyCardProps {
  property: Property;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  matchScore?: number;
  /** Above-the-fold cards (first row of a grid) → eager-load the photo so it
   *  can be the LCP element instead of waiting on lazy discovery. */
  priority?: boolean;
}

const formatPrice = (property: Property, currency: Currency) =>
  hasPrice(property.price) ? formatRent(property.price, property.priceMax, currency, !!property.priceVariesByStay) : PRICE_ON_REQUEST;

const PropertyCard = ({ property, isFavorite, onToggleFavorite, matchScore, priority = false }: PropertyCardProps) => {
  const tenant = useTenant();
  // The three most sellable declared features (registry weights), not just
  // the legacy booleans.
  const tags = cardTags(property);
  const isMobile = useIsMobile();
  const [cardApi, setCardApi] = useState<CarouselApi | null>(null);
  const [cardCurrent, setCardCurrent] = useState(0);
  const hasMultipleImages = property.images.length > 1;
  const promotions = usePromotions();
  const featured = promotions.featured.has(property.id);
  const verifiedOwner = !!property.owner_id && promotions.verifiedOwners.has(property.owner_id);

  return (
    <div className="group relative bg-card rounded-card overflow-hidden shadow-soft hover:shadow-elevated transition-shadow duration-300">
      {matchScore != null && (
        <div
          className={`absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-sm ${matchScoreClasses(matchScore).badge}`}
        >
          {matchScore}% match
        </div>
      )}
      {featured && (
        <span
          data-testid="featured-pill"
          className={`absolute left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-popover/90 text-foreground backdrop-blur-sm ${matchScore != null ? "top-11" : "top-3"}`}
        >
          Featured
        </span>
      )}
      {onToggleFavorite && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(property.id); }}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-popover/80 backdrop-blur-sm hover:bg-popover transition-colors"
          aria-label={isFavorite ? "Remove from favorites" : "Save to favorites"}
        >
          <Heart className={`h-4 w-4 transition-colors ${isFavorite ? "fill-destructive text-destructive" : "text-muted-foreground"}`} />
        </button>
      )}
      <Link to={`/properties/${property.slug}`}>
        <div className="aspect-[3/2] overflow-hidden relative">
          {isMobile && hasMultipleImages ? (
            <Carousel
              className="w-full h-full [&>div]:h-full"
              opts={{ loop: true }}
              setApi={(emblaApi) => {
                if (!emblaApi) return;
                setCardApi(emblaApi);
                setCardCurrent(emblaApi.selectedScrollSnap());
                emblaApi.on("select", () => setCardCurrent(emblaApi.selectedScrollSnap()));
              }}
            >
              <CarouselContent className="h-full ml-0">
                {property.images.map((src, idx) => (
                  <CarouselItem key={idx} className="h-full pl-0 touch-pan-y">
                    <Img
                      src={src}
                      transform={{ width: 800, quality: 70 }}
                      srcSetWidths={[400, 600, 800, 1200]}
                      sizes="100vw"
                      priority={priority && idx === 0}
                      alt={property.photos?.[idx]?.alt ?? `Rental in ${property.location} (${idx + 1})`}
                      draggable={false}
                      className="w-full h-full object-cover select-none"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : (
            <Img
              src={property.image}
              transform={{ width: 800, quality: 70 }}
              srcSetWidths={[400, 600, 800, 1200]}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
              priority={priority}
              alt={property.photos?.[0]?.alt ?? `Rental in ${property.location}`}
              className="w-full h-full object-cover"
            />
          )}
          {isMobile && hasMultipleImages && (
            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-charcoal/60 text-white text-[11px] font-medium backdrop-blur-sm pointer-events-none z-10">
              {cardCurrent + 1} / {property.images.length}
            </div>
          )}
        </div>
        <div className="p-4 pb-5">
          <div className="flex items-center gap-1 text-[13px] text-muted-foreground mb-1.5">
            <MapPin className="h-3 w-3" />{property.location}
            {property.walkingDistanceToBeachMin != null && (
              <span className="ml-2 inline-flex items-center gap-1 text-[12px]">
                <Footprints className="h-3 w-3" /> {property.walkingDistanceToBeachMin} min to beach
              </span>
            )}
          </div>
          <p className="text-primary font-semibold text-lg">
            {formatPrice(property, tenantCurrency(tenant))}
            {hasPrice(property.price) && <span className="text-[13px] font-normal text-muted-foreground">/month</span>}
          </p>
          <div className="flex items-center gap-4 mt-2 text-[13px] text-muted-foreground">
            <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" />{property.bedrooms} bd</span>
            <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{property.bathrooms} ba</span>
            {property.maxGuests && (
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{property.maxGuests} guests</span>
            )}
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {tags.map((t) => (
                <Tag key={t}>{t === "Pet Friendly" ? "Pet friendly" : t}</Tag>
              ))}
            </div>
          )}
          {property.ownerName && (
            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border/50">
              {property.ownerAvatar ? (
                <Img src={property.ownerAvatar} transform={{ width: 64, height: 64 }} alt="" className="h-5 w-5 rounded-full object-cover" />
              ) : (
                <User className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              <span className="text-[12px] text-foreground/80">Managed by {property.ownerName}</span>
              {verifiedOwner && (
                <span className="inline-flex items-center gap-0.5 text-[11px] text-terracotta-dark font-medium" title="Identity verified">
                  <BadgeCheck className="h-3.5 w-3.5" /> Identity verified
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-md bg-secondary text-muted-foreground">
    {children}
  </span>
);

export default memo(PropertyCard);
